<template>
  <a-modal
    :open="open"
    :title="title"
    trigger="click"
    @cancel="onCancel"
    @ok="onFinish"
  >
    <div v-if="type === 'createAccount'">
      {{ $t('sheng-yu-remain-ge-cheng-yuan-ming-e', [remain]) }}
    </div>
    <a-form
      ref="formInstance"
      class="mt-[20px]"
      :model="formState"
      :label-col="{ span: 4 }"
      :wrapper-col="{ span: 20 }"
    >
      <a-form-item
        :label="$t('cheng-yuan-ming-cheng')"
        name="name"
        :rules="[
          {
            required: true,
            message: $t('qing-shu-ru-cheng-yuan-ming-cheng'),
          },
        ]"
      >
        <a-input
          v-model:value="formState.name"
          :placeholder="$t('qing-shu-ru-cheng-yuan-ming-cheng')"
          :disabled="type === 'resetPassword'"
          :max-length="24"
        />
      </a-form-item>
      <a-form-item
        :label="$t('deng-lu-zhang-hao')"
        name="login_name"
        :rules="[
          {
            required: true,
            message: $t('qing-shu-ru-deng-lu-zhang-hao'),
          },
          type === 'createAccount'
            ? {
                pattern: accountNameReg,
                message: $t(
                  'zhang-hao-you-620-wei-zi-mu-huo-shu-zi-gou-cheng-bi-xu-bao-han-zi-mu'
                ),
                trigger: 'blur',
              }
            : {},
        ]"
      >
        <a-input
          v-model:value="formState.login_name"
          :placeholder="
            $t('qing-shu-ru-620-wei-zi-mu-huo-shu-zi-bi-xu-bao-han-zi-mu')
          "
          :disabled="type !== 'createAccount'"
          :max-length="24"
        />
      </a-form-item>
      <a-form-item
        :label="
          type === 'resetPassword' ? $t('xin-mi-ma') : $t('deng-lu-mi-ma')
        "
        name="login_password"
        :rules="
          type !== 'editName'
            ? [
                { required: true, message: $t('qing-shu-ru-mi-ma') },
                {
                  pattern: legacyPasswordReg,
                  message: $t('620-wei-ren-yi-shu-zi-zi-mu-te-shu-fu-hao'),
                  trigger: 'blur',
                },
              ]
            : undefined
        "
      >
        <a-input
          v-if="type !== 'editName'"
          v-model:value="formState.login_password"
          :placeholder="$t('620-wei-ren-yi-shu-zi-zi-mu-te-shu-fu-hao')"
          :max-length="24"
          type="password"
        />
        <a-input v-else value="******" disabled></a-input>
      </a-form-item>
    </a-form>
  </a-modal>
</template>
<script lang="ts" setup>
import type { FormInstance } from 'ant-design-vue'
import { createTeamMember, editTeamMember } from '~/api'
import type { Member } from '~/api/interface'

const props = defineProps<{
  type: 'createAccount' | 'editName' | 'resetPassword'
  open: boolean
  data?: Member
}>()

const emit = defineEmits(['update:open', 'update:type', 'reload'])

const { $t } = useNuxtApp()

const userInfo = useUserInfo()

const remain = computed(() => {
  return userInfo.value?.rights.sub_account_num
})

const formState = reactive({
  name: '',
  login_password: '',
  login_name: '',
})

const formInstance = ref<FormInstance>()

watch(
  () => props.open,
  (vis) => {
    if (vis) {
      if (props.type === 'createAccount') {
        formInstance.value?.resetFields()
        formState.name = ''
        formState.login_name = ''
        formState.login_password = ''
      } else if (props.data) {
        formState.name = props.data.account_name || ''
        formState.login_name = props.data.username || ''
        formState.login_password = ''
      }
    }
  }
)

const title = computed(() => {
  if (props.type === 'createAccount') {
    return $t('tian-jia-cheng-yuan')
  } else if (props.type === 'editName') {
    return $t('bian-ji-cheng-yuan')
  } else {
    return $t('zhong-zhi-mi-ma')
  }
})

async function createAccount() {
  const [encrypted, rsa] = await transformPublicKey({
    login_password: formState.login_password,
  })

  await createTeamMember({
    rsa_key_id: rsa.id,
    name: formState.name,
    login_name: formState.login_name,
    auth_data: encrypted,
  })

  message.success($t('cheng-yuan-zhang-hao-chuang-jian-cheng-gong'))
  emit('reload')
}

async function resetPassword() {
  const [encrypted, rsa] = await transformPublicKey({
    login_password: formState.login_password,
  })

  await editTeamMember({
    id: props.data!.account_id,
    rsa_key_id: rsa.id,
    auth_data: encrypted,
  })
  message.success($t('xiu-gai-cheng-gong'))
  emit('reload')
}

async function editMemberName() {
  await editTeamMember({
    id: props.data!.account_id,
    name: formState.name,
  })
  message.success($t('xiu-gai-cheng-gong'))
  emit('reload')
}

async function onFinish() {
  await formInstance.value?.validate()

  if (props.type === 'createAccount') {
    await createAccount()
  } else if (props.type === 'editName') {
    await editMemberName()
  } else if (props.type === 'resetPassword') {
    await resetPassword()
  }

  emit('update:open', false)
}

function onCancel() {
  formState.name = ''
  formState.login_name = ''
  formState.login_password = ''
  emit('update:open', false)
}
</script>
<style lang="scss" scoped></style>
